import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchById as fetchCompanyById } from "services/company.service";
import { WorkSecurityArea } from "services/work-security-areas.service";
import { WorkSecuritySettings } from "types/WorkSecuritySettings";
import { StatusBanner } from "../../ui";
import DocumentsCard from "./DocumentsCard";
import LoadingCard from "./LoadingCard";

interface AreaDocumentsProps {
  companyId: string;
  /** Absent: the documents of the company as a whole. */
  area?: WorkSecurityArea;
  companyEntity: WorkSecuritySettings;
  /** Reloads the company record. */
  refreshCompany: () => Promise<unknown>;
}

/**
 * The documents card for the company or for one area. Render it with the
 * area's id as `key`: a new area starts loading from scratch.
 */
export default function AreaDocuments({
  companyId,
  area,
  companyEntity,
  refreshCompany,
}: AreaDocumentsProps) {
  const { t } = useTranslation();
  const areaId = area?.id ?? "";

  // The company record as the area sees it: its own documents, or the company's where it has none.
  const [areaSettings, setAreaSettings] = useState<WorkSecuritySettings | null>(
    null
  );
  const [failed, setFailed] = useState(false);

  const loadAreaSettings = useCallback(async () => {
    try {
      const data = await fetchCompanyById(companyId, areaId);
      setAreaSettings(data.companyEntity);
      setFailed(false);
    } catch (err) {
      console.error("Failed to load area-scoped work security settings", err);
      setFailed(true);
    }
  }, [companyId, areaId]);

  useEffect(() => {
    if (areaId) loadAreaSettings();
  }, [areaId, loadAreaSettings]);

  if (failed) {
    return (
      <StatusBanner
        tone="error"
        title={t("work_security_settings_scope_load_error")}
      />
    );
  }
  const settings = area ? areaSettings : companyEntity;
  if (!settings) return <LoadingCard />;

  return (
    <DocumentsCard
      companyId={companyId}
      areaId={areaId}
      entity={settings}
      showIncidentAct={!area || area.hasIncidents}
      onSaved={area ? loadAreaSettings : refreshCompany}
    />
  );
}
